import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/** @jsx jsx */
import React, { forwardRef, memo, useMemo, useState } from 'react';
import { jsx } from '@emotion/core';
import { usePlatformLeafEventHandler } from '@atlaskit/analytics-next';
import GlobalTheme from '@atlaskit/theme/components';
import { getStyles } from '../internal/styles';
import EllipsisItem from './EllipsisItem';
var defaultMaxItems = 8;
var defaultBreadcrumbsLabel = 'Breadcrumbs';
var defaultEllipsisLabel = 'Show more breadcrumbs';
var toArray = React.Children.toArray;
var analyticsAttributes = {
  componentName: 'breadcrumbs',
  packageName: "@atlaskit/breadcrumbs",
  packageVersion: "11.2.0"
};

var noop = function noop() {};

var InnerBreadcrumbs = function InnerBreadcrumbs(props) {
  var _props$defaultExpande = props.defaultExpanded,
      defaultExpanded = _props$defaultExpande === void 0 ? false : _props$defaultExpande,
      isExpanded = props.isExpanded,
      _props$maxItems = props.maxItems,
      maxItems = _props$maxItems === void 0 ? defaultMaxItems : _props$maxItems,
      _props$itemsBeforeCol = props.itemsBeforeCollapse,
      itemsBeforeCollapse = _props$itemsBeforeCol === void 0 ? 1 : _props$itemsBeforeCol,
      _props$itemsAfterColl = props.itemsAfterCollapse,
      itemsAfterCollapse = _props$itemsAfterColl === void 0 ? 1 : _props$itemsAfterColl,
      _props$children = props.children,
      children = _props$children === void 0 ? [] : _props$children,
      testId = props.testId,
      _props$onExpand = props.onExpand,
      providedExpanse = _props$onExpand === void 0 ? noop : _props$onExpand,
      analyticsContext = props.analyticsContext,
      _props$mode = props.mode,
      mode = _props$mode === void 0 ? 'light' : _props$mode,
      _props$label = props.label,
      label = _props$label === void 0 ? defaultBreadcrumbsLabel : _props$label,
      _props$ellipsisLabel = props.ellipsisLabel,
      ellipsisLabel = _props$ellipsisLabel === void 0 ? defaultEllipsisLabel : _props$ellipsisLabel;

  var _useState = useState(defaultExpanded),
      _useState2 = _slicedToArray(_useState, 2),
      expanded = _useState2[0],
      setExpanse = _useState2[1];

  var isControlled = typeof isExpanded !== 'undefined';
  var handleExpansion = usePlatformLeafEventHandler(_objectSpread({
    fn: function fn(event, analyticsEvent) {
      if (!isControlled) {
        setExpanse(function (expanded) {
          return !expanded;
        });
      }

      return providedExpanse(event, analyticsEvent);
    },
    action: 'expanded',
    analyticsData: analyticsContext
  }, analyticsAttributes));
  var shouldExpand = isControlled ? isExpanded : expanded;

  var renderItemsWithEllipsis = function renderItemsWithEllipsis() {
    var allItems = childrenArray; // This defends against someone passing weird data, to ensure that if all
    // items would be shown anyway, we just show all items without the EllipsisItem

    if (itemsBeforeCollapse + itemsAfterCollapse >= allItems.length) {
      return allItems;
    }

    var beforeItems = allItems.slice(0, itemsBeforeCollapse);
    var afterItems = allItems.slice(allItems.length - itemsAfterCollapse, allItems.length);
    return [].concat(_toConsumableArray(beforeItems), [jsx(EllipsisItem, {
      key: "ellipsis",
      testId: testId && "".concat(testId, "--breadcrumb-ellipsis"),
      onClick: handleExpansion,
      label: ellipsisLabel
    })], _toConsumableArray(afterItems));
  };

  var breadcrumbStyles = useMemo(function () {
    return getStyles(mode);
  }, [mode]);
  var childrenArray = toArray(children);
  var shouldDisplayItems = shouldExpand || maxItems && childrenArray.length <= maxItems;
  return jsx("nav", {
    "aria-label": label
  }, jsx("ol", {
    "data-testid": testId,
    css: breadcrumbStyles
  }, shouldDisplayItems ? children : renderItemsWithEllipsis()));
};

var Breadcrumbs = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(function (props, ref) {
  return jsx(GlobalTheme.Consumer, null, function (tokens) {
    return jsx(InnerBreadcrumbs, _extends({}, props, {
      mode: tokens.mode,
      ref: ref
    }));
  });
}));
export default Breadcrumbs;